import type { FieldState, ReviewField } from '@hvac/contracts';
import { cn, Icon } from '@hvac/ui';
import { useEffect, useRef, useState } from 'react';

/**
 * One field, in one row.
 *
 * Every state the spec asks for is visually distinct, but distinct by a
 * *border colour and a word*, not by colour alone. A bookkeeper with colour
 * blindness has to be able to work here, and "the red ones need attention" is
 * not an interface.
 *
 * Every value is visibly editable: a pencil on each row, and one click opens
 * it. The model reading the paper can misread it, and a correction a
 * reviewer cannot find is a misreading that reaches the books.
 */

const STATE_STYLES: Record<FieldState, { border: string; badge: string; label: string }> = {
  conflict: {
    border: 'border-l-danger',
    badge: 'bg-danger-surface text-danger',
    label: 'Conflict',
  },
  invalid: { border: 'border-l-danger', badge: 'bg-danger-surface text-danger', label: 'Problem' },
  missing: { border: 'border-l-danger', badge: 'bg-danger-surface text-danger', label: 'Missing' },
  warning: { border: 'border-l-warning', badge: 'bg-warning-surface text-warning', label: 'Check' },
  unsure: { border: 'border-l-warning', badge: 'bg-warning-surface text-warning', label: 'Unsure' },
  edited: { border: 'border-l-brand-500', badge: 'bg-brand-50 text-brand-700', label: 'Edited' },
  accepted: { border: 'border-l-success', badge: '', label: '' },
  empty: { border: 'border-l-border', badge: '', label: '' },
};

export interface ReviewFieldRowProps {
  readonly field: ReviewField;
  readonly isFocused: boolean;
  readonly isEditing: boolean;
  readonly onFocus: () => void;
  readonly onStartEdit: () => void;
  readonly onCommit: (value: string | null) => void;
}

export function ReviewFieldRow({
  field,
  isFocused,
  isEditing,
  onFocus,
  onStartEdit,
  onCommit,
}: ReviewFieldRowProps) {
  const style = STATE_STYLES[field.state];
  const current = field.draftValue ?? field.predictedValue;
  // Keyed on the value, so React discards the editor's state when the
  // underlying field changes rather than a synchronising effect doing it,
  // an effect that calls setState during render cascades.
  const [text, setText] = useState(() => toText(current));
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // Focus follows the keyboard, and the row scrolls itself into view, Tab
  // through forty fields is useless if half of them are below the fold.
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (isFocused) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocused, isEditing]);

  return (
    <div
      ref={rowRef}
      role="button"
      tabIndex={-1}
      title={isEditing ? undefined : `Click to correct ${field.label.toLowerCase()}`}
      onClick={() => {
        onFocus();
        if (!isEditing) onStartEdit();
      }}
      className={cn(
        'group cursor-text border-l-2 px-3 py-1.5 transition-colors',
        style.border,
        isFocused ? 'bg-surface-sunken' : 'hover:bg-surface-sunken/50',
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="shrink-0 text-sm text-content-muted">{field.label}</span>

        {isEditing ? (
          <input
            ref={inputRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                // An emptied field is a deliberate null, not an empty string,
                // "the document does not show this" and "it shows nothing" are
                // different facts and only one of them is storable.
                onCommit(text.trim() === '' ? null : text.trim());
              }
            }}
            onBlur={() => onCommit(text.trim() === '' ? null : text.trim())}
            className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-brand-500 bg-surface px-2 py-0.5 text-right text-sm"
          />
        ) : (
          <div className="flex min-w-0 items-baseline gap-2">
            <span
              className={cn(
                'truncate text-sm',
                field.state === 'missing' ? 'text-danger' : 'text-content',
              )}
            >
              {formatValue(current)}
            </span>
            {style.label === '' ? null : (
              <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-xs', style.badge)}>
                {style.label}
              </span>
            )}
            {/* Shown only where it is actionable. A confidence badge on every
                one of forty fields is noise a reviewer learns to skip. */}
            {field.state === 'unsure' ? (
              <span className="tabular shrink-0 text-xs text-content-subtle">
                {Math.round(field.confidence * 100)}%
              </span>
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'shrink-0 text-content-subtle transition-opacity group-hover:text-brand-600 group-hover:opacity-100',
                isFocused ? 'text-brand-600 opacity-100' : 'opacity-40',
              )}
            >
              <Icon name="pencil" className="size-3.5" />
            </span>
          </div>
        )}
      </div>

      {/* The why, not just the what. A warning a reviewer cannot act on is a
          warning they will learn to dismiss. */}
      {field.issues.length > 0 && !isEditing ? (
        <p
          className={cn(
            'mt-0.5 text-xs',
            field.issues.some((issue) => issue.severity === 'error')
              ? 'text-danger'
              : 'text-warning',
          )}
        >
          {field.issues.map((issue) => issue.message).join(' · ')}
        </p>
      ) : null}

      {/* What the model originally proposed, kept visible after an edit so the
          reviewer can see what they changed without undoing it. */}
      {field.draftValue !== undefined && !isEditing ? (
        <p className="mt-0.5 text-xs text-content-subtle">
          AI read: {formatValue(field.predictedValue)}
        </p>
      ) : null}
    </div>
  );
}

function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // A shape the schema does not describe. Shown as JSON rather than as
  // "[object Object]", which tells a reviewer nothing.
  return JSON.stringify(value);
}

function formatValue(value: unknown): string {
  const text = toText(value);
  return text === '' ? '-' : text;
}
